import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

const STATUS_COLORS = {
    green: theme.colors.success,
    orange: theme.colors.warning,
    red: theme.colors.error,
    grey: theme.colors.light,
}

// ──────────────────────────────────────────────
// Dot indicator (used in the summary line)
// ──────────────────────────────────────────────

const Dot = ({ status, icon, label, active, onPress }) => {
    const color = STATUS_COLORS[status] ?? STATUS_COLORS.grey
    const textColor = active ? theme.colors.primary : theme.colors.grey
    return (
        <Pressable
            onPress={onPress}
            style={{
                alignItems: "center",
                paddingVertical: 6,
                paddingHorizontal: 4,
                borderRadius: 8,
                backgroundColor: active ? theme.colors.lighter : "transparent",
                flex: 1,
                minWidth: 50,
            }}
        >
            <View
                style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: color,
                    marginBottom: 4,
                }}
            />
            <FontAwesome5 name={icon} size={11} color={textColor} />
            <TextM
                fontSize={9}
                color={textColor}
                fontWeight={active ? "Bold" : "Regular"}
                style={{ marginTop: 1, textAlign: "center" }}
            >
                {label}
            </TextM>
        </Pressable>
    )
}

// ──────────────────────────────────────────────
// Detail helpers
// ──────────────────────────────────────────────

const InfoRow = ({ label, value, color }) => (
    <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 3 }}>
        <TextM fontSize={12} color={theme.colors.grey}>{label}</TextM>
        <TextM fontSize={12} fontWeight="Bold" color={color ?? theme.colors.primaryDark}>{value}</TextM>
    </View>
)

const TagChip = ({ emoji, label, count, type }) => (
    <View
        style={{
            backgroundColor: type === "positive" ? theme.colors.successLight : theme.colors.warningLight,
            borderRadius: 10,
            paddingHorizontal: 7,
            paddingVertical: 2,
            marginRight: 4,
            marginBottom: 4,
        }}
    >
        <TextM fontSize={10} color={type === "positive" ? theme.colors.success : theme.colors.warning}>
            {emoji} {label} x{count}
        </TextM>
    </View>
)

// ──────────────────────────────────────────────
// Detail panels
// ──────────────────────────────────────────────

const STATE_LABELS = { healthy: "Sain", watch: "Surveillance", at_risk: "A risque", critical: "Critique" }
const CATEGORY_LABELS = { promoter: "Promoteur", passive: "Passif", detractor: "Detracteur" }

const QualityWatchPanel = ({ data }) => (
    <View>
        <InfoRow label="Etat" value={STATE_LABELS[data.health_state] ?? "Aucune evaluation"} color={STATUS_COLORS[data.status]} />
        {data.last_evaluated_at && (
            <InfoRow label="Derniere evaluation" value={new Date(data.last_evaluated_at).toLocaleDateString("fr-FR")} />
        )}
        <InfoRow label="Degradations" value={data.degraded_count} color={data.degraded_count > 0 ? theme.colors.warning : undefined} />
        <InfoRow label="Critiques" value={data.critical_count} color={data.critical_count > 0 ? theme.colors.error : undefined} />
        {data.recent_signals?.map((s, i) => (
            <TextM key={i} fontSize={11} color={theme.colors.warning} style={{ marginTop: 1 }}>- {s}</TextM>
        ))}
    </View>
)

const SatisfactionPanel = ({ data }) => (
    <View>
        <InfoRow label="Moyenne (3 mois)" value={data.average != null ? `${data.average} / 5` : "—"} color={STATUS_COLORS[data.status]} />
        <InfoRow label="Taux de reponse" value={`${data.response_count} / ${data.total_sessions}`} />
        {data.last_score != null && (
            <InfoRow label="Derniere note" value={`${data.last_score}/5 — ${new Date(data.last_date).toLocaleDateString("fr-FR")}`} />
        )}
        {data.negative_comments?.map((c, i) => (
            <TextM key={i} wrap fontSize={11} color={theme.colors.error} style={{ fontStyle: "italic", marginTop: 2 }}>
                {c.satisfaction}/5 — {Array.isArray(c.comment) ? c.comment.join(", ") : c.comment}
            </TextM>
        ))}
    </View>
)

const TutorSatisfactionPanel = ({ data }) => (
    <View>
        <InfoRow
            label="Ratio positif"
            value={data.positive_ratio != null ? `${Math.round(data.positive_ratio * 100)}% (${data.positive_count}+ / ${data.negative_count}-)` : "—"}
            color={STATUS_COLORS[data.status]}
        />
        {data.top_tags?.length > 0 && (
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 4, marginBottom: 4 }}>
                {data.top_tags.map((tag, i) => <TagChip key={i} {...tag} />)}
            </View>
        )}
        {data.last_tutor_comment && (
            <TextM wrap fontSize={11} color={theme.colors.primaryDark} style={{ fontStyle: "italic", marginTop: 2 }}>
                "{data.last_tutor_comment}"
            </TextM>
        )}
    </View>
)

const AbsencesPanel = ({ data }) => (
    <View>
        <InfoRow label="Non declarees" value={data.undeclared_count} color={STATUS_COLORS[data.status]} />
        <InfoRow label="Declarees" value={data.declared_count} />
        <InfoRow label="Rattrapages en attente" value={data.pending_recoveries} color={data.pending_recoveries > 0 ? theme.colors.warning : undefined} />
        <InfoRow label="Rattrapages expires" value={data.expired_recoveries} color={data.expired_recoveries > 0 ? theme.colors.error : undefined} />
    </View>
)

const NpsPanel = ({ data }) => (
    <View>
        <InfoRow label="Score" value={data.score != null ? `${data.score} / 10` : "—"} color={STATUS_COLORS[data.status]} />
        {data.category && <InfoRow label="Categorie" value={CATEGORY_LABELS[data.category] ?? data.category} />}
        {data.date && <InfoRow label="Date" value={new Date(data.date).toLocaleDateString("fr-FR")} />}
        {data.comment && (
            <TextM wrap fontSize={11} color={theme.colors.primaryDark} style={{ fontStyle: "italic", marginTop: 2 }}>
                "{data.comment}"
            </TextM>
        )}
    </View>
)

const StabilityPanel = ({ data }) => (
    <View>
        {data.objectives?.length === 0 && (
            <TextM fontSize={11} color={theme.colors.grey}>Aucun objectif en cours</TextM>
        )}
        {data.objectives?.map((obj, i) => {
            const color = STATUS_COLORS[obj.status] ?? STATUS_COLORS.grey
            return (
                <View key={i} style={{ borderLeftWidth: 2, borderLeftColor: color, paddingLeft: 8, marginBottom: 8 }}>
                    <TextM fontSize={12} fontWeight="Bold" color={theme.colors.primaryDark}>{obj.objective_label}</TextM>
                    <InfoRow label="Tuteur principal" value={obj.main_tutor_name} />
                    <InfoRow label="Meme tuteur" value={`${obj.same_tutor_percentage}% (${obj.total_occurrences} seances)`} />
                    <InfoRow label="Tuteurs differents" value={obj.distinct_tutor_count} color={obj.distinct_tutor_count >= 3 ? color : undefined} />
                    {obj.substitution_count > 0 && (
                        <InfoRow label="Remplacements" value={obj.substitution_count} color={theme.colors.warning} />
                    )}
                </View>
            )
        })}
    </View>
)

// ──────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────

const BLOCS = [
    { key: "quality_watch", icon: "heartbeat", label: "Qualite", Panel: QualityWatchPanel },
    { key: "student_satisfaction", icon: "smile", label: "Satisfaction eleve", Panel: SatisfactionPanel },
    { key: "tutor_satisfaction", icon: "tags", label: "Evaluation tuteur", Panel: TutorSatisfactionPanel },
    { key: "absences", icon: "user-times", label: "Absences", Panel: AbsencesPanel },
    { key: "nps", icon: "chart-line", label: "NPS", Panel: NpsPanel },
    { key: "tutor_stability", icon: "users", label: "Stabilite tuteur", Panel: StabilityPanel },
]

export const StudentHealthSection = ({ data }) => {
    const [activeKey, setActiveKey] = useState(null)

    if (!data) return null

    const activeBloc = BLOCS.find((b) => b.key === activeKey)
    const ActivePanel = activeBloc?.Panel

    return (
        <View style={{ width: "100%" }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                {BLOCS.map((b) => (
                    <Dot
                        key={b.key}
                        status={data[b.key]?.status}
                        icon={b.icon}
                        label={b.label}
                        active={activeKey === b.key}
                        onPress={() => setActiveKey(activeKey === b.key ? null : b.key)}
                    />
                ))}
            </View>

            {ActivePanel && data[activeKey] && (
                <View
                    style={{
                        marginTop: 10,
                        borderTopWidth: 1,
                        borderTopColor: theme.colors.lightMore,
                        paddingTop: 10,
                    }}
                >
                    {<ActivePanel data={data[activeKey]} />}
                </View>
            )}
        </View>
    )
}
